import {useCallback, useEffect, useState} from "react";
import {Button, Card, Form, Input, List, message, Modal, Select, Space, Tag} from "antd";
import PageHeading from "../../shared/containers/PageHeading";
import {getAdministrators, inviteAdministrator, revokeInvitation, updateAdministratorAccess} from "../../shared/services/administrators";
import useAuth from "../../shared/hooks/useAuth";
import styles from "./administrators-page.module.scss";

const roleColor = {SUPER_ADMIN: "purple", ADMIN: "blue"};
const statusColor = {ACTIVE: "green", DISABLED: "red", PENDING: "gold", ACCEPTED: "green", REVOKED: "red", EXPIRED: "default"};

const AdministratorsPage = () => {
    const {user: currentUser} = useAuth();
    const [data, setData] = useState({admins: [], invitations: []});
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await getAdministrators()); }
        catch (error) { message.error(error?.response?.data?.message || "Could not load administrators"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const invite = async (values) => {
        setSubmitting(true);
        try {
            const result = await inviteAdministrator(values);
            const emailSent = result.delivery?.email?.sent;
            const whatsappSent = result.delivery?.whatsapp?.sent;
            message.success(`Invitation created${emailSent ? " and emailed" : ""}${whatsappSent ? "; admin WhatsApp sent" : ""}`);
            form.resetFields();
            setInviteOpen(false);
            await load();
        } catch (error) { message.error(error?.response?.data?.message || "Could not create invitation"); }
        finally { setSubmitting(false); }
    };

    const updateAccess = async (admin, patch) => {
        try {
            await updateAdministratorAccess(admin.id, {...admin, ...patch});
            message.success("Administrator access updated");
            await load();
        } catch (error) { message.error(error?.response?.data?.message || "Could not update access"); }
    };

    const revoke = (invitation) => Modal.confirm({
        title: "Revoke invitation?",
        content: invitation.email,
        okText: "Revoke",
        okType: "danger",
        onOk: async () => { await revokeInvitation(invitation.id); message.success("Invitation revoked"); await load(); },
    });

    return <>
        <PageHeading title="Administrators" subtitle="ERP access and invitations">
            <Button type="primary" onClick={() => setInviteOpen(true)}>Invite administrator</Button>
        </PageHeading>
        <section className={styles.section}>
            <h2>Administrators</h2>
            <List loading={loading} grid={{gutter: 16, xs: 1, sm: 1, md: 2, xl: 3}}
                dataSource={data.admins} locale={{emptyText: "No administrators found"}}
                renderItem={(admin) => <List.Item><Card className={styles.card}>
                    <div className={styles.titleRow}><strong>{[admin.firstname, admin.lastname].filter(Boolean).join(" ") || admin.username}</strong><Tag color={statusColor[admin.status]}>{admin.status}</Tag></div>
                    <p className={styles.email}>{admin.email}</p>
                    <Space wrap>
                        <Select value={admin.role} disabled={admin.id === currentUser.id} onChange={(role) => updateAccess(admin, {role})} options={[{value:"ADMIN",label:"ADMIN"},{value:"SUPER_ADMIN",label:"SUPER_ADMIN"}]}/>
                        <Button disabled={admin.id === currentUser.id} danger={admin.status === "ACTIVE"} onClick={() => updateAccess(admin, {status: admin.status === "ACTIVE" ? "DISABLED" : "ACTIVE"})}>{admin.status === "ACTIVE" ? "Disable" : "Enable"}</Button>
                    </Space>
                </Card></List.Item>}/>
        </section>
        <section className={styles.section}>
            <h2>Invitations</h2>
            <List loading={loading} dataSource={data.invitations} locale={{emptyText: "No invitations found"}}
                renderItem={(invitation) => <List.Item className={styles.invitation} actions={invitation.status === "PENDING" ? [<Button danger type="link" onClick={() => revoke(invitation)}>Revoke</Button>] : []}>
                    <List.Item.Meta title={<span className={styles.email}>{invitation.email}</span>} description={`Expires ${new Date(invitation.expires_at).toLocaleString()}`}/>
                    <Space wrap><Tag color={roleColor[invitation.role]}>{invitation.role}</Tag><Tag color={statusColor[invitation.status]}>{invitation.status}</Tag></Space>
                </List.Item>}/>
        </section>
        <Modal title="Invite administrator" open={inviteOpen} onCancel={() => setInviteOpen(false)} footer={null} destroyOnClose>
            <Form form={form} layout="vertical" onFinish={invite} initialValues={{role:"ADMIN"}}>
                <Form.Item name="email" label="Email" rules={[{required:true,type:"email",message:"Enter a valid email address"}]}><Input inputMode="email" autoComplete="email"/></Form.Item>
                <Form.Item name="role" label="Role" rules={[{required:true}]}><Select options={[{value:"ADMIN",label:"ADMIN"},{value:"SUPER_ADMIN",label:"SUPER_ADMIN"}]}/></Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting} block>Send invitation</Button>
            </Form>
        </Modal>
    </>;
};

export default AdministratorsPage;
